/**
 * 聊天的消息区：自己滚（整页锁高，见 communicator.css 的 .ps-comm-page--chat），不再带着整页滚。
 * - 进来停在最新一条；回到这个频道时回到上次的位置（chatMemory：按账号 + 宠物 + 频道记在内存里）。
 * - 停在最底下（离底 ≤ FOLLOW_SLACK_PX）时来了新消息：跟到底；往上翻着历史时来了新消息：位置不动，只亮“有新消息”，点了回到最底下。
 * - 内容变高变矮（图片加载完、键盘弹起收起、以后往上加载更早的消息）：在最底下就留在最底下，否则视线最上面那条消息原地不动
 *   （自己保持锚点，不靠浏览器的 overflow-anchor：Safari 不一定有，两边一起动会多挪一次）。
 * - 自己发出一条（followToken 变了）：回到最底下。
 * - 滚到离顶 NEAR_TOP_PX 以内时叫 onNearTop（往上翻更早的一页）；最上面放 top（“查看更早的消息 / 没有更早的消息了”）。
 * - 停在最底下、最新一条在屏幕上时叫 onSeenLatest(那一条)（标已读用，见 ./useMarkRead）：摆好位置、跟到底、滚回最底下、点“有新消息”、自己发出一条之后都会报；
 *   往上翻着历史时来了新消息不报（那条还没看到）。同一条可能报多次，去重由调用方做。
 *   更早的消息加在上面时，视线处那条原地不动（同上面的锚点）。
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import type { MessageSummary } from "@/shared/contracts";
import { Icon } from "@/shared/ui";
import { readChatMemo, writeChatMemo, type ChatScrollMemo } from "./chatMemory";

/** 离底部这么近以内都算“在最底下”：来新消息时跟到底，不亮“有新消息” */
export const FOLLOW_SLACK_PX = 80;
/** 离顶这么近以内就去取更早的一页 */
export const NEAR_TOP_PX = 240;

export function ChatScroller({
  memoKey,
  items,
  followToken,
  lead,
  top,
  onNearTop,
  onSeenLatest,
  children,
}: {
  memoKey: string;
  /** 这个频道的消息（按显示顺序）；还没读到时是 null */
  items: MessageSummary[] | null;
  /** 自己刚发出一条：数字一变就回到最底下 */
  followToken: number;
  /** 放在消息区最上面、跟着消息一起滚走的东西（矮屏时的顶栏、公告细条、分区切换） */
  lead?: ReactNode;
  /** 消息最上面（跟着消息走）：往上翻页的提示 */
  top?: ReactNode;
  /** 滚到离顶 NEAR_TOP_PX 以内 */
  onNearTop?: () => void;
  /** 停在最底下、最新一条显示在屏幕上 */
  onSeenLatest?: (latest: MessageSummary) => void;
  children: ReactNode;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const pos = useRef<ChatScrollMemo>(readChatMemo(memoKey) ?? { atBottom: true, anchorId: null, anchorOffset: 0, lastId: null });
  const placed = useRef(false);
  const [fresh, setFresh] = useState(0);
  const lastId = items?.length ? items[items.length - 1].message_id : null;
  const latest = items?.length ? items[items.length - 1] : null;
  /** 停在最底下就报最新那一条（用最新的回调与消息，不进依赖：摆位置的几个 effect 不因回调换了而重跑） */
  const seenRef = useRef<{ latest: MessageSummary | null; onSeenLatest?: (latest: MessageSummary) => void }>({ latest, onSeenLatest });
  seenRef.current = { latest, onSeenLatest };
  const reportIfBottom = useCallback(() => {
    const { latest: m, onSeenLatest: report } = seenRef.current;
    if (m && report && pos.current.atBottom) report(m);
  }, []);

  /** 读当前位置：在不在最底下、视线最上面是哪条、离上沿多远 */
  const capture = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const p = pos.current;
    p.atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= FOLLOW_SLACK_PX;
    const top = el.getBoundingClientRect().top;
    p.anchorId = null;
    for (const node of el.querySelectorAll<HTMLElement>("[data-mid]")) {
      const box = node.getBoundingClientRect();
      if (box.bottom > top) {
        p.anchorId = node.dataset.mid ?? null;
        p.anchorOffset = box.top - top;
        break;
      }
    }
  }, []);

  /** 按记着的位置摆回去：在最底下就到最底下，否则让锚点那条回到原来离上沿的距离 */
  const settle = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const p = pos.current;
    if (!p.atBottom && p.anchorId) {
      const top = el.getBoundingClientRect().top;
      for (const node of el.querySelectorAll<HTMLElement>("[data-mid]")) {
        if (node.dataset.mid !== p.anchorId) continue;
        const delta = node.getBoundingClientRect().top - top - p.anchorOffset;
        if (Math.abs(delta) >= 1) el.scrollTop += delta;
        return;
      }
    }
    // 在最底下，或者记着的那条已经不在了：到最底下
    p.atBottom = true;
    el.scrollTop = el.scrollHeight;
  }, []);

  // 消息变了：第一次读到时摆到最新（或回到上次的位置）；之后按“在不在最底下”决定跟不跟。
  useLayoutEffect(() => {
    if (!items) return;
    const p = pos.current;
    const before = p.lastId;
    p.lastId = lastId;
    if (!placed.current) {
      placed.current = true;
      settle();
      // 离开时停在历史里、回来时又多了消息：位置不动，亮“有新消息”
      if (!p.atBottom && before && lastId && before !== lastId) {
        const i = items.findIndex((m) => m.message_id === before);
        setFresh(i >= 0 ? items.length - 1 - i : 1);
      }
      reportIfBottom();
      return;
    }
    const wasBottom = p.atBottom;
    settle();
    reportIfBottom();
    if (wasBottom || lastId === before || !lastId) return;
    const i = before ? items.findIndex((m) => m.message_id === before) : -1;
    const added = i >= 0 ? items.length - 1 - i : 1;
    if (added > 0) setFresh((n) => n + added);
  }, [items, lastId, settle, reportIfBottom]);

  // 自己发出一条：回到最底下（回来的那条消息随后到，照“在最底下”跟过去）
  const seenToken = useRef(followToken);
  useLayoutEffect(() => {
    if (seenToken.current === followToken) return;
    seenToken.current = followToken;
    pos.current.atBottom = true;
    setFresh(0);
    settle();
    reportIfBottom();
  }, [followToken, settle, reportIfBottom]);

  // 消息区或内容变高变矮（图片加载完、键盘弹起收起）：摆回去
  useEffect(() => {
    const el = scrollRef.current;
    const inner = innerRef.current;
    if (!el || !inner || typeof ResizeObserver !== "function") return;
    const ro = new ResizeObserver(() => {
      if (placed.current) settle();
    });
    ro.observe(el);
    ro.observe(inner);
    return () => ro.disconnect();
  }, [settle]);

  // 离开这个频道时记下位置（滚动时也随手记）
  useEffect(() => () => {
    if (placed.current) writeChatMemo(memoKey, { ...pos.current });
  }, [memoKey]);

  const onScroll = () => {
    if (!placed.current) return;
    capture();
    if (pos.current.atBottom) {
      setFresh(0);
      reportIfBottom();
    }
    writeChatMemo(memoKey, { ...pos.current });
    if (scrollRef.current && scrollRef.current.scrollTop <= NEAR_TOP_PX) onNearTop?.();
  };

  const jumpToLatest = () => {
    const el = scrollRef.current;
    if (!el) return;
    pos.current.atBottom = true;
    setFresh(0);
    el.scrollTop = el.scrollHeight;
    reportIfBottom();
    // 按钮随即消失：焦点交给消息区，不掉到页面最前面
    el.focus({ preventScroll: true });
  };

  return (
    <div className="ps-comm-chat">
      {/* 消息区可以用键盘滚（tabIndex），读屏按“消息记录”这一块读 */}
      <div className="ps-comm-scroll" ref={scrollRef} onScroll={onScroll} tabIndex={0} role="region" aria-label="消息记录">
        <div className="ps-comm-scroll__inner" ref={innerRef}>
          {lead ? <div className="ps-comm-scroll__lead">{lead}</div> : null}
          <div className="ps-comm-scroll__body">
            {top}
            {children}
          </div>
        </div>
      </div>
      {/* 一直在的 status 区，有新消息时才放按钮：读屏会念“有新消息” */}
      <div className="ps-comm-fresh" role="status">
        {fresh > 0 ? (
          <button type="button" className="ps-comm-fresh__btn" onClick={jumpToLatest}>
            有新消息
            <Icon name="chevron" size={14} strokeWidth={2.4} />
          </button>
        ) : null}
      </div>
    </div>
  );
}
